import {
  Box,
  ClickAwayListener,
  Divider,
  Menu,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Quill, { DeltaStatic, Sources } from "quill";
import { forwardRef, useEffect, useRef, useState } from "react";
import { v4 } from "uuid";
import QuillToolBar from "./QuillToolbarV2";
import ReactQuill from "react-quill";
import PropTypes from "prop-types";
import { extractStringFromHtml } from "utilz/helpers";
import { ReactQuillProps } from "react-quill";
interface QuillEditorProps extends Omit<ReactQuillProps, "onChange"> {
  name?: string;
  onChange: (
    { field, value }: { field: string; value: string },
    meta?: {
      value: string;
      delta: DeltaStatic;
      source: Sources;
      editor: ReactQuill.UnprivilegedEditor;
    },
  ) => void;
  isRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  showToolbar?: boolean;
  maxHeight?: string; // New prop to control max height
  minHeight?: string;
  height?: string;
}

const Delta = Quill.import("delta");
export const QuillEditorV2 = forwardRef(
  ({ onChange, onBlur, ...props }: QuillEditorProps, customRef: any) => {
    const onChangeHandler = (
      value: string,
      delta: DeltaStatic,
      source: Sources,
      editor: ReactQuill.UnprivilegedEditor,
    ) => {
      onChange(
        {
          field: props.name || "",
          value: extractStringFromHtml(value).length > 0 ? value : "",
        },
        {
          value,
          delta,
          source,
          editor,
        },
      );
    };

    const theme = useTheme();
    const editorHeight = props?.height || "100%";

    return (
      <Box
        sx={{
          width: "100%",
          height: editorHeight,
          minHeight: props?.minHeight || "300px",
          maxHeight: props?.maxHeight || "100%",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${theme.palette.neutral["010"]}`,
          borderRadius: "8px",
          overflow: "hidden",
          padding: "16px",
          gap: "12px",
        }}
      >
        {props?.showToolbar && <QuillToolBar />}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden", // Prevent outer overflow
            "& .ql-container": {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "none !important",
              backgroundColor: "#FFF",
              overflow: "hidden", // Important: prevents double scroll
            },
            "& .ql-editor": {
              flex: 1,
              overflowY: "auto", // <-- Enables inner scrolling
              minHeight: "100px",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              border: props.error ? "1px solid #EF4C54" : "1px solid #EDEDED",
              backgroundColor: "#F6F6F5",
              borderRadius: "4px",
              padding: "12px", // Optional
            },
            "& .ql-snow.ql-toolbar button.ql-active, .ql-snow .ql-toolbar button.ql-active, .ql-snow.ql-toolbar .ql-picker-label.ql-active, .ql-snow .ql-toolbar .ql-picker-label.ql-active, .ql-snow.ql-toolbar .ql-picker-item.ql-selected, .ql-snow .ql-toolbar .ql-picker-item.ql-selected":
              {
                backgroundColor: "#EDEDED",
                color: "black",
              },
            "& .ql-editor > p": {
              color: "#767B85",
            },
            "& .ql-editor.ql-blank::before": {
              paddingLeft: "16px",
            },
          }}
        >
          <ReactQuill
            ref={customRef}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%", // Must be 100% for it to fill container
            }}
            theme="snow"
            value={props.value}
            onChange={onChangeHandler}
            placeholder={`${props.placeholder || ""}${
              props.isRequired ? " *" : ""
            }`}
            modules={{
              toolbar: props.showToolbar
                ? {
                    container: "#quill-toolbar-v2",
                  }
                : false,
            }}
            {...props}
          />
        </Box>

        {props?.error && (
          <Typography pl="16px" variant="body-small" color={"error"}>
            {props?.errorMessage || "This is required field"}
          </Typography>
        )}
      </Box>
    );
  },
);
